/**
 * Puffy stickers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stickers`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const puffyStickersPack: ToilePack = {
    slug: 'puffy-stickers',
    category: 'sticker',
    name: 'Puffy stickers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'heart', label: 'Puffy heart', subject: 'A chunky heart'},
        {key: 'cloud', label: 'Puffy cloud', subject: 'A cloud with a sleepy face'},
        {key: 'strawberry', label: 'Puffy strawberry', subject: 'A strawberry with a leafy top'},
    ],
    styles: [
        {key: 'holo', label: 'Holographic', style: 'holographic sticker, iridescent rainbow sheen across the surface, glossy', palette: 'Iridescent silver shifting through pink, cyan and gold', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'puffy', label: 'Puffy', style: 'puffy 3D sticker, pillowed shape with soft rounded edges, glossy vinyl surface', palette: 'Bright candy colours with a soft highlight', framing: 'Each sticker isolated on a plain ground, its raised edge showing'},
        {key: 'clear', label: 'Clear', style: 'clear sticker, the design printed on transparent film with a white underlayer, the edge visible', palette: 'Bright flat colour with a visible clear margin', framing: 'Each sticker isolated on a pale ground with the transparent edge showing'},
    ],
    directions: ['as a donut instead', 'in pastel candy colours', 'for a water bottle'],
    directionHint: 'try: as a donut instead',
    seo: {
        title: 'Puffy Stickers: Heart, Cloud, Strawberry Domed',
        metaDescription: 'Nine puffy stickers — a puffy heart, a puffy cloud with a face, a puffy strawberry — in holo, puffy, and clear finishes.',
        h1: 'Puffy stickers: heart, cloud, and strawberry with a domed pillow look',
        tagline: 'Nine pillowed stickers, three shapes, three finishes each.',
        intro: 'The pillowed sticker: a puffy heart, a puffy cloud with a face, a puffy strawberry, each drawn with the rounded, domed look of a real 3D puffy sticker. Alongside the puffy finish, each also comes in holo and clear for comparison. Ask for a different shape, and the same three finishes carry it.',
        useCases: [
            'decorating a water bottle or lunchbox',
            "a kid's sticker sheet with a 3D look",
            'a phone case or planner accent',
            'a printable puffy sticker sheet',
            'a gift sticker set for a younger sibling or child',
        ],
        faq: [
            {q: 'Do these actually come out puffy and 3D when printed?', a: 'The design is illustrated to look domed and pillowed; a genuinely raised 3D surface needs printing onto real puffy/3D sticker resin sheets, not standard paper.'},
            {q: "What's the difference between puffy, holo, and clear here?", a: 'Puffy is the domed 3D look, holo is a rainbow-sheen finish, and clear is a transparent background with only the design printed — all three are included for every shape.'},
            {q: 'Can I get a different shape than heart, cloud, or strawberry?', a: 'Yes — the direction field takes any shape, rendered in the same three finishes.'},
            {q: 'What sticker material gives a real puffy effect?', a: 'Look for 3D puffy sticker sheets (a raised resin coating) made for inkjet printers, available from most craft and print shops.'},
            {q: "Are these appropriate for a kids' sticker sheet?", a: 'Yes — the soft, rounded look is designed to read as friendly and kid-appropriate.'},
        ],
    },
}
